const SERVICE_CODE_KEY = 'serviceCode'

// 获取 pathname 的第一段路径（去掉空段）
function getFirstPathSegment(pathname: string) {
  const segments = pathname.split('/').filter(Boolean)
  return segments[0] || ''
}

// 从地址中提取 serviceCode（地址格式使用 @xxx，返回值不带 @）
export function extractServiceCodeFromPathname(pathname: string) {
  const firstSegment = getFirstPathSegment(pathname || '')
  if (firstSegment.startsWith('@') && firstSegment.length > 1) {
    return firstSegment.slice(1)
  }
  return ''
}

// 规范化 serviceCode：去空白并移除可能存在的前导 @
function normalizeServiceCode(serviceCode: string) {
  const code = (serviceCode || '').trim()
  if (!code) return ''
  return code.startsWith('@') ? code.slice(1) : code
}

// 从 localStorage 读取 serviceCode，并做规范化
function getStoredServiceCode() {
  return normalizeServiceCode(localStorage.getItem(SERVICE_CODE_KEY) || '')
}

// 获取当前可用于接口参数的 serviceCode（不带 @）
export function getServiceCode() {
  return getStoredServiceCode()
}

// 将 serviceCode 转换为地址前缀（始终带 @）
function getServiceCodePathPrefix(serviceCode: string) {
  return `/@${normalizeServiceCode(serviceCode)}`
}

// 移除 pathname 中的 /@serviceCode 前缀，返回业务真实路径
export function getPathWithoutServiceCode(pathname: string) {
  const serviceCode = normalizeServiceCode(
    extractServiceCodeFromPathname(pathname || '')
  )
  if (!serviceCode) return pathname || '/'
  const prefix = getServiceCodePathPrefix(serviceCode)
  const rest = (pathname || '').slice(prefix.length)
  return rest || '/'
}

// 初始化 serviceCode：
// 1) 若 URL 中带 @serviceCode，则写入本地（不带 @）；
// 2) 若 URL 不带但本地有值，则自动把当前地址补成 /@serviceCode/...
// 返回最终生效的 serviceCode（不带 @）
export function initServiceCodeFromLocation() {
  const serviceCodeFromPath = extractServiceCodeFromPathname(
    window.location.pathname
  )
  if (serviceCodeFromPath) {
    localStorage.setItem(SERVICE_CODE_KEY, serviceCodeFromPath)
    return serviceCodeFromPath
  }

  const storedServiceCode = getStoredServiceCode()
  if (storedServiceCode) {
    const pathname = window.location.pathname || '/'
    const pathnameWithCode = `${getServiceCodePathPrefix(storedServiceCode)}${
      pathname.startsWith('/') ? pathname : `/${pathname}`
    }`
    const nextUrl = `${pathnameWithCode}${window.location.search || ''}${
      window.location.hash || ''
    }`
    window.history.replaceState(window.history.state, '', nextUrl)
  }

  return storedServiceCode
}

// 给站内相对路径补 /@serviceCode 前缀（已带前缀或无本地值则原样返回）
export function appendServiceCodeToPath(path: string) {
  if (!path) return path
  if (/^https?:\/\//i.test(path)) return path

  const serviceCode = getStoredServiceCode()
  if (!serviceCode) return path
  const prefix = getServiceCodePathPrefix(serviceCode)
  if (path.startsWith(prefix)) return path

  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${prefix}${normalized}`
}

// 给请求 URL 追加 serviceCode 查询参数（值不带 @）
export function appendServiceCodeToRequestUrl(url: string) {
  if (!url) return url
  const serviceCode = getStoredServiceCode()
  if (!serviceCode) return url

  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}serviceCode=${encodeURIComponent(serviceCode)}`
}
